import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { ThemeEvents } from '@theme/events';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#mutationObserver.observe(this, { attributes: true });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    if (this.dataset.sessionAnchor === 'true') {
      this.#loadRecommendations();
    } else {
      this.#intersectionObserver.observe(this);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#activeFetch?.abort();
  }

  /**
   * Refresh cart-anchored recommendations from the current Shopify cart.
   * Existing cards are removed immediately when their product enters the cart,
   * then the section is hydrated again so a different eligible product can take
   * their place.
   *
   * @param {CustomEvent} event
   */
  #handleCartUpdate = (event) => {
    if (this.dataset.cartAnchor !== 'true') return;

    const cartItems = event.detail?.resource?.items;
    if (!Array.isArray(cartItems)) return;

    const cartProductIds = [
      ...new Set(
        cartItems
          .map((item) => String(item?.product_id || ''))
          .filter((productId) => /^\d+$/.test(productId))
      ),
    ];
    const cartProductIdSet = new Set(cartProductIds);

    for (const productCard of this.querySelectorAll('product-card[data-product-id], .b20-card[data-product-id]')) {
      if (!cartProductIdSet.has(productCard.dataset.productId || '')) continue;
      productCard.closest('.resource-list__item')?.remove();
    }

    for (const list of this.querySelectorAll('[data-has-recommendations]')) {
      list.dataset.hasRecommendations = String(Boolean(list.querySelector('.resource-list__item')));
    }

    this.#activeFetch?.abort();
    this.#cachedRecommendations = {};
    this.#mutationObserver.disconnect();
    this.dataset.cartProductIds = JSON.stringify(cartProductIds);
    this.dataset.productId = cartProductIds[0] || '';
    this.dataset.recommendationsPerformed = 'false';
    this.#mutationObserver.observe(this, { attributes: true });

    if (cartProductIds.length === 0) {
      this.classList.add('hidden');
      return;
    }

    this.classList.remove('hidden');
    this.#loadRecommendations();
  };

  /**
   * Load the product recommendations
   */
  async #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent, sessionAnchor, cartAnchor } = this.dataset;
    const productIds = [];

    if (cartAnchor === 'true') {
      try {
        const cartProductIds = JSON.parse(this.dataset.cartProductIds || '[]');
        if (Array.isArray(cartProductIds)) productIds.push(...cartProductIds);
      } catch {
        // Ignore malformed optional cart context and continue with other anchors.
      }
    }
    if (productId) productIds.push(productId);
    if (sessionAnchor === 'true') productIds.push(...RecentlyViewed.getProducts());

    const uniqueProductIds = [...new Set(productIds.map(String).filter((id) => /^\d+$/.test(id)))];
    if (uniqueProductIds.length === 0 || !sectionId) {
      this.classList.add('hidden');
      return;
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    try {
      let lastFailure = null;
      for (const anchorProductId of uniqueProductIds) {
        const result = await this.#fetchCachedRecommendations(anchorProductId, sectionId, intent);
        if (!result.success) {
          lastFailure = result;
          continue;
        }

        if (this.#containsRecommendations(result.data)) {
          this.dataset.recommendationsPerformed = 'true';
          await morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
          return;
        }
      }

      // The Theme Editor can request a section id unavailable to the renderer.
      if (lastFailure && !Shopify.designMode) {
        this.#handleError(new Error(`Server returned ${lastFailure.status}`));
        return;
      }
      this.classList.add('hidden');
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.#handleError(error);
    }
  }

  /** @param {string} markup */
  #containsRecommendations(markup) {
    if (!markup?.trim()) return false;
    const document = new DOMParser().parseFromString(markup, 'text/html');
    return Boolean(document.querySelector('[data-has-recommendations="true"], .resource-list__item'));
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    try {
      const response = await fetch(url, { signal: abortController.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
